/**
 * Phase 128.34 — Öffentlicher Aktions-Slider.
 * Auto-Rotation alle 6 s, Swipe/Pfeil-Navigation, Punkt-Indikator, pausiert
 * bei Hover/Touch. Wird unter dem Hero-Bereich der Homepage gerendert.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTOROTATE_MS = 6000;

const PromoSlider = () => {
  const [slides, setSlides] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/promo-slides`);
        if (!cancelled) setSlides(r.data?.slides || []);
      } catch { /* silent — Slider entfällt einfach */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const total = slides.length;
  const next = useCallback(() => setIdx((i) => (i + 1) % Math.max(total, 1)), [total]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + Math.max(total, 1)) % Math.max(total, 1)), [total]);

  // Auto-Rotate
  useEffect(() => {
    if (paused || total < 2) return undefined;
    const t = setInterval(next, AUTOROTATE_MS);
    return () => clearInterval(t);
  }, [next, paused, total]);

  if (total === 0) return null;
  const slide = slides[idx];

  const isExternal = slide.cta_link && /^https?:\/\//i.test(slide.cta_link);
  const Wrapper = ({ children }) => slide.cta_link ? (
    isExternal
      ? <a href={slide.cta_link} target="_blank" rel="noopener noreferrer" className="block group">{children}</a>
      : <a href={slide.cta_link} className="block group">{children}</a>
  ) : <div>{children}</div>;

  const overlayColor = slide.accent_color || '#0c4a6e';

  return (
    <section
      className="w-full max-w-6xl mx-auto px-3 sm:px-4 py-6 md:py-8"
      data-testid="promo-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; setPaused(true); }}
      onTouchEnd={(e) => {
        if (touchStartX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (dx > 40) prev();
        else if (dx < -40) next();
        touchStartX.current = null;
        setPaused(false);
      }}
    >
      <div className="relative rounded-3xl overflow-hidden shadow-xl bg-neutral-900" data-testid={`promo-slide-${idx}`}>
        <Wrapper>
          <div className="relative aspect-[21/9] sm:aspect-[24/9] md:aspect-[28/9] w-full">
            {slide.image_url && (
              <img
                src={slide.image_url}
                alt={slide.title}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
            )}
            {/* Overlay gradient for readability */}
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(90deg, ${overlayColor}CC 0%, ${overlayColor}66 55%, transparent 100%)` }}
            />
            <div className="relative z-10 h-full flex items-center px-6 sm:px-10 md:px-14 py-6">
              <div className="max-w-xl text-white">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight drop-shadow-md">
                  {slide.title}
                </h2>
                {slide.subtitle && (
                  <p className="mt-2 md:mt-3 text-sm md:text-base text-white/90 leading-relaxed drop-shadow">
                    {slide.subtitle}
                  </p>
                )}
                {slide.cta_text && slide.cta_link && (
                  <span className="inline-flex items-center gap-2 mt-4 bg-yellow-400 text-sky-950 px-5 py-2.5 rounded-full font-bold text-sm shadow-lg group-hover:shadow-xl group-hover:-translate-y-0.5 transition-all">
                    {slide.cta_text} →
                  </span>
                )}
              </div>
            </div>
          </div>
        </Wrapper>

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={prev}
              aria-label="Vorheriger Slide"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/85 hover:bg-white text-neutral-900 rounded-full p-2 shadow-md backdrop-blur"
              data-testid="promo-slider-prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Nächster Slide"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/85 hover:bg-white text-neutral-900 rounded-full p-2 shadow-md backdrop-blur"
              data-testid="promo-slider-next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-20">
              {slides.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`transition-all rounded-full ${
                    i === idx ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/60 hover:bg-white/80'
                  }`}
                  data-testid={`promo-slider-dot-${i}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default PromoSlider;
